// Company-admin holiday calendar (Model A + statutory-holiday Phase 2).
// Split view: STATUTORY holidays are materialized from the platform's
// published pack for the company's registration country (locked, read-only —
// corrections arrive as new pack versions automatically); COMPANY holidays
// remain fully editable. The legacy country-pack import is retired — the
// mandatory jurisdiction subscription keeps the statutory rows in sync.
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CalendarDays, Plus, Loader2, Trash2, ShieldAlert, Power, Pencil, X, Check, Lock, Globe2,
} from 'lucide-react';
import { useRbacRoles } from '@/rbac/useRbac';
import { hasPermission } from '@/rbac/rbac.generated';
import {
  getHolidays, createHoliday, updateHoliday, deleteHoliday,
  getHolidaySubscriptions, addHolidaySubscription, removeHolidaySubscription,
  getHolidayImpact, resolveHolidayImpact, getHolidayImpactSettings, setHolidayImpactSettings,
  HOLIDAY_COUNTRIES, type Holiday, type HolidayType, type HolidayPackSubscription,
  type HolidayImpactItem,
} from '@/lib/hrApi';
import { getApiErrorMessage } from '@/lib/apiError';
import { findConflict } from './holidayConflict';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { markHolidaysReviewed } from '@/features/onboarding/setup/holidaysReview';

const TYPE_BADGE: Record<HolidayType, string> = {
  public: 'bg-info-soft text-info-strong',
  company: 'bg-orange-100 text-orange-700',
  optional: 'bg-muted text-muted-foreground',
};

const fmtDate = (s: string) =>
  new Date(s + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });

const thisYear = new Date().getFullYear();

export const HolidaysPage: React.FC = () => {
  const { t } = useTranslation('holidays');
  const roles = useRbacRoles();
  const canManage = hasPermission(roles, 'holiday:manage');
  const confirm = useConfirm();

  const [year, setYear] = useState(thisYear);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [subs, setSubs] = useState<HolidayPackSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const [impact, setImpact] = useState<HolidayImpactItem[]>([]);
  const [recreditMode, setRecreditMode] = useState<'auto' | 'review'>('auto');
  const [adding, setAdding] = useState(false);
  const [addingCountry, setAddingCountry] = useState(false);
  const [newCountry, setNewCountry] = useState('');
  const [form, setForm] = useState<{ name: string; date: string; type: HolidayType }>({
    name: '', date: `${thisYear}-01-01`, type: 'company',
  });
  const [editId, setEditId] = useState<string | null>(null);

  const load = async (y: number) => {
    setLoading(true);
    try {
      // Include inactive so admins can re-enable disabled ones.
      const data = await getHolidays(y, undefined);
      setHolidays([...data].sort((a, b) => a.date.localeCompare(b.date)));
    } catch {
      setNote({ kind: 'err', text: t('err_load') });
    } finally {
      setLoading(false);
    }
  };
  const loadSubs = async () => {
    try {
      // Also lazily ensures the registration-country subscription server-side
      // (and materializes the active pack on first sight).
      setSubs(await getHolidaySubscriptions());
    } catch {
      /* chips are secondary — the list still renders */
    }
  };
  const loadImpact = async () => {
    try { setImpact(await getHolidayImpact('open')); } catch { /* secondary */ }
    try { setRecreditMode(await getHolidayImpactSettings()); } catch { /* secondary */ }
  };
  useEffect(() => { if (canManage) void load(year); }, [year, canManage]);
  // The lazy-ensure may materialize statutory rows — reload the list after.
  useEffect(() => {
    if (canManage) void loadSubs().then(() => Promise.all([load(year), loadImpact()]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage]);
  // Opening this page counts as "reviewed" → completes the setup-checklist step.
  useEffect(() => { if (canManage) void markHolidaysReviewed(); }, [canManage]);

  const submitHoliday = async (allowConflict: boolean) => {
    if (!form.name.trim() || !form.date) return;
    setBusy(true); setNote(null);
    try {
      await createHoliday({ name: form.name.trim(), date: form.date, type: form.type }, allowConflict);
      setAdding(false);
      setForm({ name: '', date: `${year}-01-01`, type: 'company' });
      await load(year);
    } catch (e: any) {
      const code = e?.response?.headers?.['x-error-code'];
      const detail = e?.response?.data?.detail as string | undefined;
      // US-09: a near-duplicate is a warning, not a hard error — let HR confirm.
      if (e?.response?.status === 409 && code === 'HOLIDAY_CONFLICT') {
        setBusy(false);
        const ok = await confirm({
          title: t('holiday_conflict_title', 'Possible duplicate'),
          message: detail || t('holiday_conflict_msg', 'This looks like a duplicate of an existing holiday. Add it anyway?'),
          confirmLabel: t('add_anyway', 'Add anyway'),
        });
        if (ok) return submitHoliday(true);
        return;
      }
      setNote({ kind: 'err', text: detail || t('err_add') });
    } finally { setBusy(false); }
  };
  const onAdd = () => submitHoliday(false);

  const onToggle = async (h: Holiday) => {
    setBusy(true);
    try {
      await updateHoliday(h.id, { is_active: !h.is_active });
      setHolidays((hs) => hs.map((x) => (x.id === h.id ? { ...x, is_active: !x.is_active } : x)));
    } catch (e) { setNote({ kind: 'err', text: getApiErrorMessage(e, t('err_update')) }); }
    finally { setBusy(false); }
  };

  const onSaveEdit = async (h: Holiday, patch: Partial<Holiday>) => {
    setBusy(true);
    try {
      const updated = await updateHoliday(h.id, patch as any);
      setHolidays((hs) => hs.map((x) => (x.id === h.id ? { ...x, ...updated } : x)));
      setEditId(null);
    } catch (e) { setNote({ kind: 'err', text: getApiErrorMessage(e, t('err_save')) }); }
    finally { setBusy(false); }
  };

  const onDelete = async (h: Holiday) => {
    setBusy(true);
    try {
      await deleteHoliday(h.id);
      setHolidays((hs) => hs.filter((x) => x.id !== h.id));
    } catch (e) { setNote({ kind: 'err', text: getApiErrorMessage(e, t('err_delete')) }); }
    finally { setBusy(false); }
  };

  const onResolveImpact = async (id: string) => {
    setBusy(true); setNote(null);
    try {
      await resolveHolidayImpact(id);
      setImpact((xs) => xs.filter((x) => x.id !== id));
      setNote({ kind: 'ok', text: t('impact_resolved') });
    } catch (e) { setNote({ kind: 'err', text: getApiErrorMessage(e, t('err_resolve')) }); }
    finally { setBusy(false); }
  };

  const onSetMode = async (mode: 'auto' | 'review') => {
    const prev = recreditMode;
    setRecreditMode(mode);
    try { await setHolidayImpactSettings(mode); }
    catch (e) {
      setRecreditMode(prev);
      setNote({ kind: 'err', text: getApiErrorMessage(e, t('err_mode')) });
    }
  };

  const onAddCountry = async () => {
    if (!newCountry) return;
    setBusy(true); setNote(null);
    try {
      await addHolidaySubscription(newCountry);
      setAddingCountry(false); setNewCountry('');
      await loadSubs();
    } catch (e) { setNote({ kind: 'err', text: getApiErrorMessage(e, t('err_sub_add')) }); }
    finally { setBusy(false); }
  };

  const onRemoveCountry = async (cc: string) => {
    setBusy(true); setNote(null);
    try {
      await removeHolidaySubscription(cc);
      await loadSubs();
    } catch (e) { setNote({ kind: 'err', text: getApiErrorMessage(e, t('err_sub_remove')) }); }
    finally { setBusy(false); }
  };

  const years = useMemo(() => [thisYear - 1, thisYear, thisYear + 1], []);
  const statutory = useMemo(() => holidays.filter((h) => h.source === 'pack'), [holidays]);
  const company = useMemo(() => holidays.filter((h) => h.source !== 'pack'), [holidays]);
  // US-09: flag company holidays that clash with a statutory pack entry (same
  // holiday on a different date, a spelling variant, or a longer/shorter name),
  // so pre-existing duplicates are visibly deduped even before they're removed.
  const dupOf = useMemo(() => {
    const m = new Map<string, string>();
    company.forEach((h) => {
      const clash = findConflict(h.name, h.date, statutory);
      if (clash) m.set(h.id, clash.name);
    });
    return m;
  }, [company, statutory]);
  const availableCountries = useMemo(
    () => HOLIDAY_COUNTRIES.filter((c) => !subs.some((s) => s.country_code === c.code)),
    [subs],
  );

  if (!canManage) {
    return (
      <div className="max-w-md mx-auto mt-16 text-center">
        <ShieldAlert className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
        <h1 className="text-h3 text-charcoal">{t('admins_only')}</h1>
        <p className="text-muted-foreground mt-1">{t('no_permission')}</p>
      </div>
    );
  }

  const input = 'h-10 px-3 rounded-lg border border-border bg-card text-foreground text-sm focus:border-orange-400 focus:outline-none';
  const primaryBtn = 'h-10 px-4 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50';
  const ghostBtn = 'h-10 px-4 rounded-lg border border-border text-foreground hover:bg-muted inline-flex items-center gap-2';

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-h2 text-charcoal flex items-center gap-2"><CalendarDays className="w-6 h-6" /> {t('title')}</h1>
        <p className="text-muted-foreground mt-1">{t('subtitle')}</p>
      </div>

      {note && (
        <div className={`rounded-lg px-3 py-2 text-sm border ${
          note.kind === 'ok' ? 'bg-success-soft border-success text-success-strong' : 'bg-danger-soft border-danger text-danger-strong'
        }`}>{note.text}</div>
      )}

      {/* Controls: year + jurisdiction chips */}
      <div className="rounded-2xl border border-border bg-card p-4 flex flex-wrap items-center gap-3">
        <select className={input} value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground inline-flex items-center gap-1"><Globe2 className="w-4 h-4" /> {t('jurisdictions')}:</span>
          {subs.map((s) => (
            <span key={s.country_code}
              className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${
                s.locked ? 'bg-info-soft text-info-strong border-transparent' : 'bg-muted text-muted-foreground border-border'
              }`}
              title={s.locked ? t('mandatory') : t('extra_note')}
            >
              {s.locked && <Lock className="w-3 h-3" />}
              {s.country_code}
              {s.locked ? ` · ${t('mandatory')}` : ` · ${t('extra_inert')}`}
              {!s.locked && (
                <button className="ml-0.5 hover:text-red-600" onClick={() => onRemoveCountry(s.country_code)} disabled={busy}>
                  <X className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
          {addingCountry ? (
            <span className="inline-flex items-center gap-1">
              <select className="h-8 px-2 rounded-lg border border-border bg-card text-foreground text-xs" value={newCountry} onChange={(e) => setNewCountry(e.target.value)}>
                <option value="">—</option>
                {availableCountries.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select>
              <button className="p-1 text-success" onClick={onAddCountry} disabled={busy || !newCountry}><Check className="w-4 h-4" /></button>
              <button className="p-1 text-muted-foreground" onClick={() => setAddingCountry(false)}><X className="w-4 h-4" /></button>
            </span>
          ) : (
            availableCountries.length > 0 && (
              <button className="text-xs px-2 py-1 rounded-full border border-dashed border-border text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                onClick={() => setAddingCountry(true)}>
                <Plus className="w-3 h-3" /> {t('add_country')}
              </button>
            )
          )}
        </div>
        <div className="flex-1" />
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          {t('recredit_mode')}
          <select className={input} value={recreditMode} onChange={(e) => onSetMode(e.target.value as 'auto' | 'review')}>
            <option value="auto">{t('recredit_auto')}</option>
            <option value="review">{t('recredit_review')}</option>
          </select>
        </label>
        <button className={primaryBtn} onClick={() => setAdding((a) => !a)}>
          <Plus className="w-4 h-4" /> {t('add_holiday')}
        </button>
      </div>

      {/* Holiday impact review (Phase 3) — only when there is something open */}
      {impact.length > 0 && (
        <div className="rounded-2xl border border-amber-300 bg-card p-2">
          <div className="px-3 pt-3 pb-1">
            <h2 className="font-semibold text-foreground">{t('impact_title')}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{t('impact_hint')}</p>
          </div>
          <ul className="divide-y divide-border">
            {impact.map((i) => (
              <li key={i.id} className="px-3 py-3 flex items-center gap-3">
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  i.impact_type === 'needs_review' ? 'bg-amber-100 text-amber-800'
                  : i.impact_type === 'locked_period' ? 'bg-danger-soft text-danger-strong'
                  : 'bg-info-soft text-info-strong'
                }`}>{t('impact_' + i.impact_type)}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-foreground">
                    {(i.changed_dates ?? []).slice(0, 3).join(', ')}
                    {i.old_days != null && i.new_days != null && (
                      <span className="text-muted-foreground"> · {t('impact_days', { old: i.old_days, new: i.new_days })}</span>
                    )}
                  </div>
                  {i.detail?.note && <div className="text-xs text-muted-foreground truncate">{i.detail.note}</div>}
                </div>
                <button className="text-sm px-3 h-8 rounded-lg border border-border hover:bg-muted" disabled={busy}
                  onClick={() => onResolveImpact(i.id)}>
                  {t('impact_resolve')}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Add form (company holidays) */}
      {adding && (
        <div className="rounded-2xl border border-border bg-card p-4 flex flex-wrap items-end gap-3">
          <label className="flex-1 min-w-[180px]">
            <span className="block text-sm font-medium text-foreground mb-1">{t('name')}</span>
            <input className={`${input} w-full`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t('name_ph')} />
          </label>
          <label>
            <span className="block text-sm font-medium text-foreground mb-1">{t('date')}</span>
            <input className={input} type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </label>
          <label>
            <span className="block text-sm font-medium text-foreground mb-1">{t('type')}</span>
            <select className={input} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as HolidayType })}>
              <option value="company">{t('type_company')}</option>
              <option value="public">{t('type_public')}</option>
              <option value="optional">{t('type_optional')}</option>
            </select>
          </label>
          <button className={primaryBtn} onClick={onAdd} disabled={busy || !form.name.trim()}>{t('save')}</button>
        </div>
      )}

      {loading ? (
        <div className="rounded-2xl border border-border bg-card py-12 flex items-center justify-center text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('loading')}
        </div>
      ) : (
        <>
          {/* Statutory (pack-managed, read-only) */}
          <div className="rounded-2xl border border-border bg-card p-2">
            <div className="px-3 pt-3 pb-1">
              <h2 className="font-semibold text-foreground flex items-center gap-2"><Lock className="w-4 h-4 text-info-strong" /> {t('statutory_title')}</h2>
              <p className="text-xs text-muted-foreground mt-0.5">{t('statutory_hint')}</p>
            </div>
            {statutory.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground text-sm">{t('statutory_empty', { year })}</div>
            ) : (
              <ul className="divide-y divide-border">
                {statutory.map((h) => (
                  <li key={h.id} className={`px-3 py-3 flex items-center gap-3 ${h.is_active ? '' : 'opacity-50'}`}>
                    <div className="w-28 text-sm text-muted-foreground shrink-0">{fmtDate(h.date)}</div>
                    <span className="flex-1 font-medium text-foreground">{h.name}</span>
                    {subs.length > 1 && h.country_code && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{h.country_code}</span>
                    )}
                    {h.observed_date && h.observed_date !== h.date && (
                      <span className="text-xs text-muted-foreground">{t('observed', { date: fmtDate(h.observed_date) })}</span>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${TYPE_BADGE[h.type] ?? TYPE_BADGE.public}`}>{t('type_' + h.type)}</span>
                    <span title={t('pack_locked')}><Lock className="w-4 h-4 text-muted-foreground" /></span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Company (editable) */}
          <div className="rounded-2xl border border-border bg-card p-2">
            <div className="px-3 pt-3 pb-1">
              <h2 className="font-semibold text-foreground">{t('company_title')}</h2>
              <p className="text-xs text-muted-foreground mt-0.5">{t('company_hint')}</p>
            </div>
            {company.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground text-sm">{t('company_empty', { year })}</div>
            ) : (
              <ul className="divide-y divide-border">
                {company.map((h) => (
                  <li key={h.id} className={`px-3 py-3 flex items-center gap-3 ${h.is_active ? '' : 'opacity-50'}`}>
                    <div className="w-28 text-sm text-muted-foreground shrink-0">{fmtDate(h.date)}</div>
                    {editId === h.id ? (
                      <InlineEdit holiday={h} onCancel={() => setEditId(null)} onSave={(patch) => onSaveEdit(h, patch)} />
                    ) : (
                      <>
                        <span className="flex-1 font-medium text-foreground">{h.name}</span>
                        {dupOf.has(h.id) && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-warning-soft text-warning-strong inline-flex items-center gap-1" title={t('dup_of', { name: dupOf.get(h.id), defaultValue: `Duplicate of statutory "${dupOf.get(h.id)}"` })}>
                            <ShieldAlert className="w-3 h-3" />{t('dup_badge', { defaultValue: 'Duplicate' })}
                          </span>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded-full ${TYPE_BADGE[h.type]}`}>{t('type_' + h.type)}</span>
                        {!h.is_active && <span className="text-xs text-muted-foreground">{t('disabled')}</span>}
                        <button className="p-1.5 text-muted-foreground hover:text-foreground" title={t('edit')} onClick={() => setEditId(h.id)}><Pencil className="w-4 h-4" /></button>
                        <button className="p-1.5 text-muted-foreground hover:text-foreground" title={h.is_active ? t('disable') : t('enable')} onClick={() => onToggle(h)}><Power className="w-4 h-4" /></button>
                        <button className="p-1.5 text-muted-foreground hover:text-red-600" title={t('delete')} onClick={() => onDelete(h)}><Trash2 className="w-4 h-4" /></button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const InlineEdit: React.FC<{ holiday: Holiday; onCancel: () => void; onSave: (p: Partial<Holiday>) => void }> = ({
  holiday, onCancel, onSave,
}) => {
  const { t } = useTranslation('holidays');
  const [name, setName] = useState(holiday.name);
  const [date, setDate] = useState(holiday.date);
  const [type, setType] = useState<HolidayType>(holiday.type);
  const input = 'h-9 px-2 rounded-lg border border-border bg-card text-foreground text-sm focus:border-orange-400 focus:outline-none';
  return (
    <div className="flex-1 flex flex-wrap items-center gap-2">
      <input className={`${input} flex-1 min-w-[140px]`} value={name} onChange={(e) => setName(e.target.value)} />
      <input className={input} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      <select className={input} value={type} onChange={(e) => setType(e.target.value as HolidayType)}>
        <option value="company">{t('type_company')}</option>
        <option value="public">{t('type_public')}</option>
        <option value="optional">{t('type_optional')}</option>
      </select>
      <button className="p-1.5 text-success" title={t('save')} onClick={() => onSave({ name: name.trim(), date, type })}><Check className="w-4 h-4" /></button>
      <button className="p-1.5 text-muted-foreground" title={t('cancel')} onClick={onCancel}><X className="w-4 h-4" /></button>
    </div>
  );
};

export default HolidaysPage;
